import { computed, onScopeDispose, ref } from 'vue'
import type { NetworkErrorState } from '@/api/network-error'

const online = ref<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true)
const currentError = ref<NetworkErrorState | null>(null)

let listenersBound = false
let stopListeners: (() => void) | null = null

function ensureListeners() {
  if (listenersBound || typeof window === 'undefined') return

  listenersBound = true

  const handleOnline = () => {
    online.value = true
    if (currentError.value?.kind === 'offline') {
      currentError.value = null
    }
  }

  const handleOffline = () => {
    online.value = false
  }

  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)
  stopListeners = () => {
    window.removeEventListener('online', handleOnline)
    window.removeEventListener('offline', handleOffline)
    listenersBound = false
    stopListeners = null
  }

  try {
    onScopeDispose(() => {
      stopListeners?.()
    })
  } catch {
    // Called outside a reactive scope (e.g. API retry path); no-op.
  }
}

export function useNetworkStatus() {
  ensureListeners()

  const offline = computed(() => !online.value)

  function reportError(state: NetworkErrorState) {
    if (!state.retryable) return

    currentError.value = state
  }

  // Retry = a full page reload. This is the ONE universally-safe recovery: it
  // re-runs every route data fetch from a clean state and — unlike replaying the
  // original Request from the interceptor — never trips the consumed/locked body
  // stream hazard on POST/PUT bodies (the same hazard the 401 pristine-clone in
  // generated-client.ts guards against). The banner error clears because the
  // reload discards this module's in-memory `currentError`.
  function retry(): void {
    if (typeof window !== 'undefined') {
      window.location.reload()
    } else {
      currentError.value = null
    }
  }

  function clear(): void {
    currentError.value = null
  }

  return {
    online,
    offline,
    currentError,
    reportError,
    retry,
    clear,
  }
}
